import * as schemas from "@common/schemas";
import * as schemasv2 from "@common/schemas/v2";
import { Step, StepActions, StepFactory } from "../steps/v2/step";
import { createFlows, CreateSystemFlowsParams, FlowHooks, FlowKey, FlowOptions } from "./flow";
import { FlowHookActions, FlowHookFactory } from "../hooks/hook";
import { Flow, Section } from "./types";

export interface BuildAllFlowsParams {
  tourPlayback: schemas.TourPlayback;
  stepActions: StepActions;
  flowHookActions: FlowHookActions;
  systemFlowParams: CreateSystemFlowsParams;
  flowFlexibility: schemas.FlowFlexibility;
}

export const buildAllFlows = ({
  tourPlayback,
  stepActions,
  flowHookActions,
  systemFlowParams,
  flowFlexibility,
}: BuildAllFlowsParams): Record<FlowKey, Flow> => {
  const flows = createFlows(tourPlayback, systemFlowParams);

  return Object.fromEntries(
    Object.entries(flows).map(([key, value]) => [
      key,
      buildFlow({
        key: key as FlowKey,
        sections: value.sections,
        stepActions,
        flowHookActions,
        options: value.options,
        systemFlowParams,
        hooks: value.hooks,
        flowFlexibility,
      }),
    ])
  ) as Record<FlowKey, Flow>;
};

export interface BuildFlowParams {
  key: FlowKey;
  sections: schemasv2.Section[];
  stepActions: StepActions;
  flowHookActions: FlowHookActions;
  systemFlowParams: CreateSystemFlowsParams;
  options: FlowOptions;
  hooks?: FlowHooks;
  flowFlexibility: schemas.FlowFlexibility;
}

const buildFlow = ({
  key,
  sections,
  stepActions,
  flowHookActions,
  options,
  hooks,
  flowFlexibility,
}: BuildFlowParams): Flow => {
  const buildHooks = (hooks?: FlowHooks) => {
    if (!hooks) {
      return undefined;
    }

    return {
      onFinish: FlowHookFactory.create(hooks.onFinish, flowHookActions),
    };
  };

  return {
    key,
    options,
    hooks: buildHooks(hooks),
    sections: buildSections(sections, stepActions, flowFlexibility),
  };
};

export const buildSections = (
  sections: schemasv2.Section[],
  stepActions: StepActions,
  flowFlexibility: schemas.FlowFlexibility
): Section[] => {
  return sections.map((section, index) => {
    return buildSection(section, stepActions, flowFlexibility, index);
  });
};

export const buildSection = (
  section: schemasv2.Section,
  stepActions: StepActions,
  flowFlexibility: schemas.FlowFlexibility,
  index: number = 0
): Section => {
  return { ...section, steps: buildSteps(section, stepActions, flowFlexibility), index };
};

export const buildSteps = (
  section: schemasv2.Section,
  stepActions: StepActions,
  flowFlexibility: schemas.FlowFlexibility
): Step[] => {
  return StepFactory.createList({ steps: section.steps, actions: stepActions, flowFlexibility });
};
